/**
 * Simple API helper with type support
 * Note: This is a simplified API helper for the prospector module
 * Uses fetch with JSON handling
 */

// Determine the base URL based on environment
const getBaseUrl = () => {
  // In production, use relative URL
  if (process.env.NODE_ENV === 'production') {
    return '/api/prospector';
  }
  // In development, use the localhost URL
  return 'http://localhost:5005/api/prospector';
};

const API_URL = getBaseUrl();

/**
 * Generic API client for making HTTP requests
 */
export const api = {
  /**
   * Make a GET request
   */
  async get<T>(endpoint: string): Promise<T> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token // Keep for backward compatibility
      }
    });

    if (!response.ok) {
      // Try to get error message from response
      try {
        const errorData = await response.json();
        throw new Error(`API Error: ${response.status} - ${errorData.message || errorData.error || 'Unknown error'}`);
      } catch (e) {
        // If we can't parse the error JSON, just throw the status
        throw new Error(`API Error: ${response.status}`);
      }
    }

    return response.json() as Promise<T>;
  },

  /**
   * Make a POST request
   */
  async post<T>(endpoint: string, data: any): Promise<T> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token // Keep for backward compatibility
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      // Try to get error message from response
      try {
        const errorData = await response.json();
        throw new Error(`API Error: ${response.status} - ${errorData.message || errorData.error || 'Unknown error'}`);
      } catch (e) {
        // If we can't parse the error JSON, just throw the status
        throw new Error(`API Error: ${response.status}`);
      }
    }

    return response.json() as Promise<T>;
  },

  /**
   * Make a PUT request
   */
  async put<T>(endpoint: string, data: any): Promise<T> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token // Keep for backward compatibility
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      // Try to get error message from response
      try {
        const errorData = await response.json();
        throw new Error(`API Error: ${response.status} - ${errorData.message || errorData.error || 'Unknown error'}`);
      } catch (e) {
        // If we can't parse the error JSON, just throw the status
        throw new Error(`API Error: ${response.status}`);
      }
    }

    return response.json() as Promise<T>;
  },

  /**
   * Make a PATCH request
   */
  async patch<T>(endpoint: string, data: any): Promise<T> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token // Keep for backward compatibility
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      // Try to get error message from response
      try {
        const errorData = await response.json();
        throw new Error(`API Error: ${response.status} - ${errorData.message || errorData.error || 'Unknown error'}`);
      } catch (e) {
        // If we can't parse the error JSON, just throw the status
        throw new Error(`API Error: ${response.status}`);
      }
    }

    return response.json() as Promise<T>;
  },

  /**
   * Make a DELETE request
   */
  async delete<T>(endpoint: string): Promise<T> {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Authentication required');
    }

    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token // Keep for backward compatibility
      }
    });

    if (!response.ok) {
      // Try to get error message from response
      try {
        const errorData = await response.json();
        throw new Error(`API Error: ${response.status} - ${errorData.message || errorData.error || 'Unknown error'}`);
      } catch (e) {
        // If we can't parse the error JSON, just throw the status
        throw new Error(`API Error: ${response.status}`);
      }
    }

    // For DELETE operations that don't return data
    if (response.status === 204) {
      return {} as T;
    }

    return response.json() as Promise<T>;
  }
};